/**
 * MediaStudioDialog — the studio "Refiner" surface (per Trinity §1.2).
 *
 * Layout (immersive rework, 2026-07-01; action hierarchy refined 2026-07-26):
 *
 *   ┌─────────────────────────────────────────────────────────────┐
 *   │ Title                 [Create post] [Edit · More]             │  ← thin top bar
 *   ├─────────────────────────────────────────────────────────────┤
 *   │                                                             │
 *   │                     ASSET HERO (stage)                      │  ← full-bleed,
 *   │              object-contain · crop/text in-place            │    centered
 *   │                                                             │
 *   ├─────────────────────────────────────────────────────────────┤
 *   │   ✨ Try an edit · Adjust · Animate · Remix…                 │  ← inside composer
 *   │        Tell Mora what to change…                         ↑  │
 *   └─────────────────────────────────────────────────────────────┘
 *
 * Design rationale:
 *   - Single-column asset hero — the asset is the subject, the composer is
 *     the hero interaction (in-place edit, ChatGPT-style). No split rail.
 *   - The title bar shows ONLY the filename. Model / aspect / dimensions /
 *     duration live ONLY behind the ⓘ Info popover — showing them twice
 *     (title bar AND popover) was redundant clutter (founder review).
 *   - Durable workflow actions live in the header: Create post is primary,
 *     while Edit and More share the standard page-header action group.
 *   - Prompt-backed AI starters live inside the composer so their effect is
 *     visible and editable before submission. Animate remains an explicit
 *     direct action. The rail finger-scrolls with an edge fade on mobile.
 *   - No Variations/duplicate button (founder: remove the "duplicate"), no
 *     focus-mode toggle, no Regenerate, no floating hover chip over the
 *     asset (it duplicated the always-visible action row below). Only one
 *     "Make video" surface exists now. Iteration goes through the composer.
 *   - Info (ⓘ) and overflow (⋯) triggers open a richer panel on click, so
 *     they carry aria-label only — no separate hover Tooltip. Stacking a
 *     Tooltip on top of a Popover/DropdownMenu trigger was both redundant
 *     (the click reveals far more than the tooltip ever could) and the
 *     source of a real bug: the tooltip could render detached/stuck after
 *     the panel opened. Undo/Redo/Download/Close are one-shot actions with
 *     no click-revealed content, so they keep their tooltips.
 */
import {
  ArrowClockwiseIcon,
  ArrowCounterClockwiseIcon,
  ArrowLeftIcon,
  ArrowsClockwiseIcon,
  CardsThreeIcon,
  CheckIcon,
  CropIcon,
  DotsThreeIcon,
  DownloadSimpleIcon,
  FlipHorizontalIcon,
  FlipVerticalIcon,
  PencilSimpleIcon,
  PlusIcon,
  SparkleIcon,
  TextTIcon,
} from '@phosphor-icons/react';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import {
  type AISuggestion,
  IMAGE_AI_SUGGESTIONS,
  MediaStudioAISuggestionBlock,
  VIDEO_AI_SUGGESTIONS,
} from './MediaStudioAISuggestionBlock';
import { MediaStudioAssetStage } from './MediaStudioAssetStage';
import {
  MediaStudioCustomPromptInput,
  type MediaStudioCustomPromptInputHandle,
} from './MediaStudioCustomPromptInput';
import { MediaStudioOriginalPromptCard } from './MediaStudioOriginalPromptCard';
import { type SuggestionChip, SuggestionChipRow } from './SuggestionChips';
import type { AnyElement, LogoElement } from './TextOverlayTypes';
import type { ShapeElement, TextElement } from './text-editor/useTextEditorStore';
import { isLogoElement, isShapeElement } from './text-editor/useTextEditorStore';
import { type MediaStudioPayload } from './types';

import { PlatformBrandIcon } from '@/components/icons/platform-brand-icon';
import { PostCreateMenu } from '@/components/posts/PostCreateMenu';
import { ProjectAssignmentSubmenu } from '@/components/projects/ProjectAssignmentMenu';
import { Button } from '@/components/ui/button';
import { CircleIconButton } from '@/components/ui/circle-icon-button';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  useDialogContentContainer,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { InspectorBreadcrumb } from '@/components/ui/inspector/bodies/InspectorBreadcrumb';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useBrandKitContext } from '@/contexts/BrandKitContext';
import { useEffectiveLockedFields } from '@/hooks/useEffectiveLockedFields';
import type { OverlayLayer } from '@/lib/media-studio/overlay-types';
import { TEXT_FADE_RIGHT } from '@/lib/ui-tokens';
import { mediaService } from '@/services/media/mediaService';
import { useImageDialogStore } from '@/stores/useImageDialogStore';
import { downloadBlob } from '@/utils/downloadBlob';
import { logger } from '@/utils/logger';

export type { MediaStudioMeta, MediaStudioPayload } from './types';

interface BaseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inline?: boolean;
  savedToLibrary?: boolean;
  onDownload?: () => void;
  onSaveToPlan?: () => void;
  onPushToShopify?: () => void;
  /** Undo the most recent in-place edit. Wired by ImageBody from useImageDialogStore. */
  onUndo?: () => void;
  /** Re-apply an undone edit. */
  onRedo?: () => void;
  /** Drives the Undo button's enabled state — typically `history.length > 0`. */
  canUndo?: boolean;
  /** Drives the Redo button's enabled state — typically `future.length > 0`. */
  canRedo?: boolean;
  /**
   * Legacy chip handler — only invoked when chip's namedAction is `animate`,
   * `push-shopify`, or `caption`. The Animate handoff button is rendered as
   * a synthetic chip with namedAction='animate' for image kinds.
   */
  onChipAction?: (chip: SuggestionChip) => void | Promise<void>;
  /** Free-form prompt submitted from the right-rail composer. */
  onCustomPrompt?: (prompt: string) => Promise<void> | void;
  /** Start a manual draft with the current image. */
  onCreatePost?: () => void;
  /** Continue in Home with the current image attached as AI context. */
  onCreateWithMora?: () => void;
  /** Locks both create-post intents while a manual draft is being made. */
  isCreatingPost?: boolean;
  onCropSave?: (blob: Blob) => Promise<void> | void;
  /** Single-flight lock state — owned by the parent (ImageBody / VideoBody). */
  isOperating?: boolean;
  /** Id of the currently-running chip operation (drives per-chip spinner). */
  activeOperationId?: string | null;
  /**
   * When false, Variations is hidden (uploaded images can't reroll without
   * a prompt to reroll from). Defaults to true.
   */
  regenerable?: boolean;
  /** Artifact id forwarded to `<SaveToProjectMenu>` when present. */
  artifactId?: string | null;
  /** Artifact kind for the project_files row. Defaults to 'media'. */
  artifactKind?: string;
  breadcrumb?: { rootLabel: string; rootHref?: string | undefined; leafLabel: string } | undefined;
}

interface ImageDialogProps extends BaseDialogProps {
  payload: Extract<MediaStudioPayload, { kind: 'image' }>;
  onVariations?: () => void;
  /** Image-only — rotate the asset 90° clockwise. */
  onRotate90?: () => void;
  /** Image-only — mirror the asset horizontally (one-shot, like rotate). */
  onFlipHorizontal?: () => void;
  /** Image-only — mirror the asset vertically (one-shot, like rotate). */
  onFlipVertical?: () => void;
  /** Called when the user saves a text overlay from the text editor. Image-only. */
  onTextOverlaySave?: (blob: Blob) => Promise<void> | void;
}

interface VideoDialogProps extends BaseDialogProps {
  payload: Extract<MediaStudioPayload, { kind: 'video' }>;
  onVariations?: () => void;
  onExtend?: () => void;
}

export type MediaStudioDialogProps = ImageDialogProps | VideoDialogProps;

function getEffectiveBreadcrumb(
  breadcrumb: BaseDialogProps['breadcrumb'],
  payloadTitle: string,
): NonNullable<BaseDialogProps['breadcrumb']> | undefined {
  if (breadcrumb) return breadcrumb;
  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/creatives/assets')) {
    return { rootLabel: 'Assets', rootHref: '/creatives/assets', leafLabel: payloadTitle };
  }
  return undefined;
}

function DialogBody(props: MediaStudioDialogProps) {
  const {
    payload,
    savedToLibrary = false,
    onDownload,
    onPushToShopify,
    isOperating = false,
    activeOperationId = null,
  } = props;
  const { brandKit } = useBrandKitContext();
  const _defaultFontFamily = brandKit?.font_primary ?? 'Inter';
  // Every nested DropdownMenu in this dialog (Create post, Edit tools, More)
  // portals into the enclosing DialogContent's own DOM subtree instead of
  // document.body — see DialogContentContainerContext's doc comment in
  // dialog.tsx for why that's what actually fixes the cascade-close bug.
  const dialogContainer = useDialogContentContainer();

  // BI-5-03: disable the recolor chip when a brand color is INHERITED-locked
  // (ancestor corporate lock). Empty inherited set → colorLocked false → the
  // chip row is byte-identical to today (consumer/corporate-own kits).
  const { inheritedLockedFields } = useEffectiveLockedFields();
  const colorLocked = inheritedLockedFields.some((k) =>
    [
      'primaryColor',
      'secondaryColor',
      'accentColor',
      'primary_color',
      'secondary_color',
      'accent_color',
    ].includes(k),
  );

  // Fallback download: when no onDownload prop is provided, fetch the asset
  // as a Blob and save with explicit MIME so the file lands as a real
  // .mp4 / .png — fixes "QuickTime cannot open files in 'data' format".
  const defaultDownload = useCallback(async () => {
    const url =
      payload.kind === 'video'
        ? (payload.context.videoUrl ?? '')
        : (payload.context.imageUrl ?? '');
    if (!url) return;
    const ext = payload.kind === 'video' ? 'mp4' : 'png';
    const filename = `mora-${payload.kind}-${Date.now()}.${ext}`;

    void downloadBlob(url, filename);
  }, [payload]);
  const resolvedOnDownload = onDownload ?? defaultDownload;

  const composerRef = useRef<MediaStudioCustomPromptInputHandle>(null);
  const [showSavedChip, setShowSavedChip] = useState(savedToLibrary);
  const [cropMode, setCropMode] = useState(false);
  const [cropSaving, setCropSaving] = useState(false);
  // Phase 4 — when the image dialog store carries openMode:'text' (set by
  // generate-image-view when hasOverlay=true), auto-enter text-overlay mode
  // on mount so the editor opens pre-populated with the persisted layers.
  // We read from the store once via getState() so we don't re-render on
  // every store change; a ref guards the one-shot so reset on asset-change
  // (assetKey effect below) doesn't re-trigger spuriously.
  const storeOpenMode = useImageDialogStore.getState().payload?.openMode;
  const [textOverlayMode, setTextOverlayMode] = useState(
    () => payload.kind === 'image' && storeOpenMode === 'text',
  );
  const [textOverlaySaving, setTextOverlaySaving] = useState(false);
  // null = not yet fetched for the current asset. Fetched lazily on text-mode
  // entry so the 99% of dialog opens that never touch text don't pay a DB round-trip.
  const [overlayLayers, setOverlayLayers] = useState<OverlayLayer[] | null>(null);
  const fetchedAssetIdRef = useRef<string | null>(null);
  const [composerDraft, setComposerDraft] = useState('');

  // Stable key identifying the current asset — image uses assetId, video uses jobId.
  const assetKey = payload.kind === 'image' ? payload.context.assetId : payload.context.jobId;

  useEffect(() => {
    if (!savedToLibrary) {
      setShowSavedChip(false);
      return;
    }
    setShowSavedChip(true);
    const timer = window.setTimeout(() => setShowSavedChip(false), 4000);
    return () => window.clearTimeout(timer);
  }, [savedToLibrary, assetKey]);

  // Reset overlay state whenever the dialog opens for a different asset.
  // Also re-apply openMode:'text' if the new asset was also opened with
  // that intent (re-read store at change time, not from the initial snapshot).
  useEffect(() => {
    setOverlayLayers(null);
    fetchedAssetIdRef.current = null;
    const nextOpenMode = useImageDialogStore.getState().payload?.openMode;
    if (payload.kind === 'image' && nextOpenMode === 'text') {
      setTextOverlayMode(true);
    }
  }, [assetKey, payload.kind]);

  // Phase 4 — respond to imperative requestTextMode() calls from ImageBody's
  // "Add headline" / "Add logo" actions. textModeRequest is an incrementing
  // counter so each call fires a distinct effect even if the dialog is
  // already in text mode.
  const textModeRequest = useImageDialogStore((s) => s.textModeRequest);
  const clearTextModeRequest = useImageDialogStore((s) => s.clearTextModeRequest);
  useEffect(() => {
    if (textModeRequest > 0 && payload.kind === 'image') {
      setTextOverlayMode(true);
      clearTextModeRequest();
    }
  }, [textModeRequest, payload.kind, clearTextModeRequest]);

  // Fetch persisted overlay layers once per asset, on open, so the stage can
  // preview saved text and the text editor can reload it.
  useEffect(() => {
    if (payload.kind !== 'image') return;
    const assetId = payload.context.assetId;
    if (!assetId || fetchedAssetIdRef.current === assetId) return;
    fetchedAssetIdRef.current = assetId;

    mediaService
      .getOverlayLayers(assetId)
      .then((layers) => setOverlayLayers(layers))
      .catch((err) => {
        logger.error('Failed to load overlay layers', err);
        setOverlayLayers([]);
      });
  }, [payload]);

  // Convert OverlayLayer[] → AnyElement[] for the text editor's initialLayers prop.
  const overlayLayersAsTextElements: AnyElement[] = useMemo(
    () =>
      (overlayLayers ?? []).map((layer): AnyElement => {
        if (layer.kind === 'logo') {
          return {
            id: layer.id,
            kind: 'logo' as const,
            logoUrl: layer.logoUrl,
            logoVariant: layer.logoVariant,
            x: layer.x,
            y: layer.y,
            width: layer.width,
            height: layer.height,
            rotation: layer.rotation,
            scaleX: layer.scaleX,
            scaleY: layer.scaleY,
            opacity: layer.opacity,
            zIndex: layer.zIndex ?? 0,
          } satisfies LogoElement;
        }
        if (layer.kind === 'shape') {
          return {
            id: layer.id,
            kind: 'shape' as const,
            shapeType: layer.shapeType,
            x: layer.x,
            y: layer.y,
            width: layer.width,
            height: layer.height,
            rotation: layer.rotation,
            fill: layer.fill,
            opacity: layer.opacity,
            feather: layer.feather,
            cornerRadius: layer.cornerRadius,
            points: layer.points,
            innerRadius: layer.innerRadius,
            strokeWidth: layer.strokeWidth,
            strokeColor: layer.strokeColor,
            zIndex: layer.zIndex ?? 0,
          } satisfies ShapeElement;
        }
        return {
          id: layer.id,
          text: layer.text,
          x: layer.x,
          y: layer.y,
          width: layer.width,
          height: layer.height,
          fontSize: layer.fontSize,
          fontFamily: layer.fontFamily,
          fontWeight: layer.fontWeight,
          isItalic: layer.isItalic ?? false,
          isUnderline: layer.isUnderline ?? false,
          textAlign: layer.textAlign,
          verticalAlign: layer.verticalAlign,
          textTransform: layer.textTransform,
          lineHeight: layer.lineHeight,
          letterSpacing: layer.letterSpacing,
          fill: layer.fill,
          contrastMode: layer.contrastMode,
          contrastColor: layer.contrastColor,
          contrastIntensity: layer.contrastIntensity,
          contrastPadding: layer.contrastPadding ?? { top: 0, right: 0, bottom: 0, left: 0 },
          boxRoundness: layer.boxRoundness ?? 0,
          scaleX: layer.scaleX ?? 1,
          scaleY: layer.scaleY ?? 1,
          boxShapeType: layer.boxShapeType,
          boxFeather: layer.boxFeather,
          rotation: layer.rotation,
          zIndex: layer.zIndex ?? 0,
        } satisfies TextElement;
      }),
    [overlayLayers],
  );

  // Save text/logo overlay layers as non-destructive metadata (no compositing).
  const handleLayersSave = async (
    layers: (TextElement | LogoElement | ShapeElement)[],
    stageWidth: number,
  ) => {
    if (payload.kind !== 'image') return;
    const assetId = payload.context.assetId;
    if (!assetId) return;

    const overlays: OverlayLayer[] = layers.map((el) => {
      if (isLogoElement(el)) {
        return {
          id: el.id,
          kind: 'logo' as const,
          stageWidth,
          x: el.x,
          y: el.y,
          width: el.width,
          height: el.height,
          rotation: el.rotation,
          scaleX: el.scaleX,
          scaleY: el.scaleY,
          logoUrl: el.logoUrl,
          logoVariant: el.logoVariant,
          opacity: el.opacity,
          zIndex: el.zIndex,
        };
      }
      if (isShapeElement(el)) {
        return {
          id: el.id,
          kind: 'shape' as const,
          stageWidth,
          shapeType: el.shapeType,
          x: el.x,
          y: el.y,
          width: el.width,
          height: el.height,
          rotation: el.rotation,
          fill: el.fill,
          opacity: el.opacity,
          feather: el.feather,
          cornerRadius: el.cornerRadius,
          points: el.points,
          innerRadius: el.innerRadius,
          strokeWidth: el.strokeWidth,
          strokeColor: el.strokeColor,
          zIndex: el.zIndex,
        };
      }
      return {
        id: el.id,
        kind: 'text' as const,
        stageWidth,
        x: el.x,
        y: el.y,
        width: el.width,
        height: el.height,
        rotation: el.rotation,
        text: el.text,
        fontSize: el.fontSize,
        fontFamily: el.fontFamily,
        fontWeight: el.fontWeight,
        isItalic: el.isItalic,
        isUnderline: el.isUnderline,
        lineHeight: el.lineHeight,
        letterSpacing: el.letterSpacing,
        textAlign: el.textAlign,
        verticalAlign: el.verticalAlign,
        textTransform: el.textTransform,
        fill: el.fill,
        contrastMode: el.contrastMode,
        contrastColor: el.contrastColor,
        contrastIntensity: el.contrastIntensity,
        contrastPadding: el.contrastPadding,
        boxRoundness: el.boxRoundness,
        boxShapeType: el.boxShapeType,
        boxFeather: el.boxFeather,
        scaleX: el.scaleX,
        scaleY: el.scaleY,
        zIndex: el.zIndex,
      };
    });

    try {
      await mediaService.updateOverlayLayers(assetId, overlays);
      setOverlayLayers(overlays);
      setTextOverlayMode(false);
    } catch (err) {
      logger.error('Failed to persist overlay layers', err);
      // Keep in-memory layers so a retry from the same session works.
      setOverlayLayers(overlays);
      toast.error(
        "Couldn't save text overlay. Your changes are kept in this session. Try Done again.",
        { id: 'overlay-save-failed' },
      );
    }
  };

  const handlePickSuggestion = (suggestion: AISuggestion) => {
    setComposerDraft((current) => {
      const existing = current.trimEnd();
      return existing ? `${existing}\n${suggestion.prefill}` : suggestion.prefill;
    });
    queueMicrotask(() => composerRef.current?.focusAndCaretEnd());
  };

  const handlePrefillChip = (chip: SuggestionChip) => {
    if (!chip.prompt) return;
    handlePickSuggestion({ id: chip.id, label: chip.label, prefill: chip.prompt });
  };

  const suggestions = payload.kind === 'image' ? IMAGE_AI_SUGGESTIONS : VIDEO_AI_SUGGESTIONS;

  // Secondary tools are out of context while a focused sub-editor (crop / text)
  // is open — mirror the old inline-toolbar disabled condition.
  const editingDisabled = isOperating || cropMode || textOverlayMode;

  // Image-only physical transforms, folded into the overflow menu.
  const imageProps = payload.kind === 'image' ? (props as ImageDialogProps) : null;
  const hasTransforms = Boolean(
    imageProps &&
    (imageProps.onRotate90 || imageProps.onFlipHorizontal || imageProps.onFlipVertical),
  );
  // Overflow has content when there's any asset edit tool to offer.
  const hasOverflow =
    payload.kind === 'image'
      ? true
      : Boolean((props as VideoDialogProps).onExtend || onPushToShopify);

  // Shared "details" body (prompt card + model/aspect/dimensions/duration).
  // Rendered in TWO places: the desktop Info popover AND the mobile ⋯ More
  // menu — extracted so the two never drift. On mobile the toolbar collapses
  // to just Edit + ⋯ + Close (progressive disclosure); Undo/Redo/Download and
  // these details all live inside the ⋯ menu so the bar stays uncluttered on
  // a narrow viewport.
  const detailsBody = (
    <>
      {payload.context.prompt ? (
        <MediaStudioOriginalPromptCard prompt={payload.context.prompt} />
      ) : null}
      <dl className="space-y-1.5 text-detail">
        {payload.meta.model ? (
          <div className="flex items-center justify-between gap-3">
            <dt className="text-muted-foreground">Model</dt>
            <dd className="truncate text-foreground/85">{payload.meta.model}</dd>
          </div>
        ) : null}
        <div className="flex items-center justify-between gap-3">
          <dt className="text-muted-foreground">Aspect</dt>
          <dd className="text-foreground/85">{payload.meta.aspect}</dd>
        </div>
        {payload.kind === 'image' && payload.meta.dimensions ? (
          <div className="flex items-center justify-between gap-3">
            <dt className="text-muted-foreground">Dimensions</dt>
            <dd className="text-foreground/85">{payload.meta.dimensions}</dd>
          </div>
        ) : null}
        {payload.kind === 'video' && payload.meta.duration ? (
          <div className="flex items-center justify-between gap-3">
            <dt className="text-muted-foreground">Duration</dt>
            <dd className="text-foreground/85">{payload.meta.duration}</dd>
          </div>
        ) : null}
      </dl>
    </>
  );
  const effectiveBreadcrumb = getEffectiveBreadcrumb(props.breadcrumb, payload.title);

  return (
    // Immersive single-column stage (ChatGPT-inspired): the asset fills the
    // frame; a thin top bar floats over it (title + icon cluster) and a
    // floating composer + primary actions dock over the bottom. No split rail —
    // the prompt + meta live behind the Info popover, secondary tools behind
    // the overflow menu. The composer is the hero interaction.
    <TooltipProvider delayDuration={300}>
      <div className="relative flex h-full min-h-0 flex-col">
        {/* ── Thin top bar ─────────────────────────────────────────────
            Title/meta on the left, icon cluster on the right. Sits above the
            stage (not overlaid) so it never occludes the asset. No border —
            header/stage/footer are now ONE unified bg-background surface
            (ChatGPT image-viewer model); a hairline here would draw a seam
            between two things that are deliberately the same color, and the
            footer already has no border-t against the stage either.

            Hidden during crop / text-overlay mode — EditModeShell (rendered
            inside the stage below) has its OWN Cancel/title/Done header, so
            this one was a genuine double-header: two title bars stacked, one
            of them (Edit/Details/Close) entirely non-functional mid-edit
            anyway since editingDisabled greys most of it out. Wasted a full
            44px row for nothing and read as broken on mobile (live review). */}
        {!cropMode && !textOverlayMode ? (
          <div className="relative flex h-12 shrink-0 items-center gap-2 px-4">
            {/* Mirrors PageHeader.tsx's structure exactly: `.page-title-centered`
              absolute-centers the title on mobile and reverts to normal static
              flex flow at md: (left-aligned, immediately after ←) — the SAME
              shared behavior Project/Discover use, so this modal's desktop
              header reads as "current normal spaced layout" per the Header Law,
              not a mobile treatment stretched to desktop. Unlike routed pages
              the ← stays visible at every breakpoint — a full-bleed modal has
              no sidebar/breadcrumb to fall back on for dismissal. */}
            <div className="z-10 flex shrink-0 items-center">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <CircleIconButton
                      size="header"
                      ariaLabel="Back"
                      className="header-nav-btn rounded-full"
                      onClick={() => props.onOpenChange(false)}
                    >
                      <ArrowLeftIcon weight="regular" />
                    </CircleIconButton>
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom">Back</TooltipContent>
              </Tooltip>
            </div>

            {/* CENTER — mobile shows the leaf beside the back affordance;
              desktop restores the full parent breadcrumb. Model / aspect /
              dimensions live in the ⋯ → Details panel. */}
            <div className="page-title-centered pointer-events-none min-w-0 md:flex md:flex-1 md:justify-start">
              <div className="pointer-events-auto min-w-0">
                {effectiveBreadcrumb ? (
                  <InspectorBreadcrumb
                    breadcrumbs={[
                      {
                        label: effectiveBreadcrumb.rootLabel,
                        href: effectiveBreadcrumb.rootHref,
                      },
                      { label: effectiveBreadcrumb.leafLabel },
                    ]}
                    fallbackTitle={payload.title}
                  />
                ) : (
                  <h2 className={`text-sm font-medium text-foreground/90 ${TEXT_FADE_RIGHT}`}>
                    {payload.title}
                  </h2>
                )}
              </div>
            </div>

            {/* RIGHT — Header Law: Edit (primary editing) + ⋯ (everything else:
              undo/redo, download, add-to-project, details), replacing the old
              7-icon inline cluster. Fused into ONE glassy pill on mobile only;
              desktop reverts to the normal spaced layout (`.header-action-pill`
              un-fuses at md: via CSS, `md:gap-1.5` gives the two buttons their
              usual breathing room). */}
            <div className="z-10 ml-auto flex shrink-0 items-center gap-2">
              {showSavedChip ? (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-muted/40 px-2.5 py-1 text-detail text-muted-foreground transition-opacity duration-300 ease-out animate-in fade-in">
                  <CheckIcon className="size-3 text-foreground/60" weight="bold" />
                  <span className="hidden sm:inline">Saved to library</span>
                </span>
              ) : null}

              {props.onCreatePost && props.onCreateWithMora ? (
                // Two-door choice — only available where a chat-reference handoff
                // exists (image today). Video has no reference-attachment support
                // yet, so it gets the single honest action below instead of a
                // dropdown with a door that doesn't actually do anything.
                <PostCreateMenu
                  source="library"
                  align="end"
                  onCreateWithMora={props.onCreateWithMora}
                  onCreatePost={props.onCreatePost}
                  isCreatingPost={props.isCreatingPost}
                  container={dialogContainer}
                  trigger={
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={editingDisabled || props.isCreatingPost}
                      aria-label="Create a post"
                    >
                      <PlusIcon data-icon="inline-start" weight="regular" />
                      <span className="hidden sm:inline">Create post</span>
                    </Button>
                  }
                />
              ) : props.onCreatePost ? (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={editingDisabled || props.isCreatingPost}
                  aria-label="Create a post"
                  onClick={props.onCreatePost}
                >
                  <PlusIcon data-icon="inline-start" weight="regular" />
                  <span className="hidden sm:inline">Create post</span>
                </Button>
              ) : null}

              {/* TOOL chrome — the unified media dialog is a toolbar inside a
                surface, so `.header-action-pill` (capsule on touch, bare from
                `md:`) rather than the page-header capsule. See
                `docs/design-system/TOOLBAR-DOCTRINE.md`. */}
              <div className="header-action-pill inline-flex items-center">
                {/* Edit tools — crop / add text / rotate / flip / Shopify (image),
                  extend / Shopify (video). Pencil, not dots: reads as "edit this
                  asset", the same signifier as the post-inspector edit trigger. */}
                {hasOverflow ? (
                  <DropdownMenu>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <DropdownMenuTrigger asChild>
                          <CircleIconButton
                            size="header"
                            ariaLabel="Edit"
                            className="data-[state=open]:bg-muted data-[state=open]:text-foreground"
                            disabled={editingDisabled}
                          >
                            <PencilSimpleIcon weight="regular" />
                          </CircleIconButton>
                        </DropdownMenuTrigger>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">Edit</TooltipContent>
                    </Tooltip>
                    <DropdownMenuContent align="end" className="w-48" container={dialogContainer}>
                      {payload.kind === 'image' ? (
                        <>
                          <DropdownMenuItem onClick={() => setCropMode(true)}>
                            <CropIcon className="size-4" weight="regular" />
                            Crop
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setTextOverlayMode(true)}>
                            <TextTIcon className="size-4" weight="regular" />
                            Add text
                          </DropdownMenuItem>
                          {hasTransforms || onPushToShopify ? <DropdownMenuSeparator /> : null}
                          {imageProps?.onRotate90 ? (
                            <DropdownMenuItem onClick={imageProps.onRotate90}>
                              <ArrowsClockwiseIcon className="size-4" weight="regular" />
                              Rotate 90°
                            </DropdownMenuItem>
                          ) : null}
                          {imageProps?.onFlipHorizontal ? (
                            <DropdownMenuItem onClick={imageProps.onFlipHorizontal}>
                              <FlipHorizontalIcon className="size-4" weight="regular" />
                              Flip horizontal
                            </DropdownMenuItem>
                          ) : null}
                          {imageProps?.onFlipVertical ? (
                            <DropdownMenuItem onClick={imageProps.onFlipVertical}>
                              <FlipVerticalIcon className="size-4" weight="regular" />
                              Flip vertical
                            </DropdownMenuItem>
                          ) : null}
                          {onPushToShopify ? (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={onPushToShopify}>
                                <PlatformBrandIcon platform="shopify" className="size-4" />
                                Push to Shopify
                              </DropdownMenuItem>
                            </>
                          ) : null}
                        </>
                      ) : (
                        <>
                          {(props as VideoDialogProps).onExtend ? (
                            <DropdownMenuItem onClick={(props as VideoDialogProps).onExtend}>
                              <ArrowsClockwiseIcon className="size-4" weight="regular" />
                              Extend
                            </DropdownMenuItem>
                          ) : null}
                          {onPushToShopify ? (
                            <DropdownMenuItem onClick={onPushToShopify}>
                              <PlatformBrandIcon platform="shopify" className="size-4" />
                              Push to Shopify
                            </DropdownMenuItem>
                          ) : null}
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : null}

                {/* ⋯ More — everything that isn't primary editing: undo / redo
                  (only once there's edit history), download, add to project, and
                  the Details meta panel. One overflow across all breakpoints. */}
                <DropdownMenu>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <DropdownMenuTrigger asChild>
                        <CircleIconButton
                          size="header"
                          ariaLabel="More"
                          className="data-[state=open]:bg-muted data-[state=open]:text-foreground"
                        >
                          <DotsThreeIcon weight="bold" />
                        </CircleIconButton>
                      </DropdownMenuTrigger>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">More actions</TooltipContent>
                  </Tooltip>
                  <DropdownMenuContent
                    align="end"
                    className="max-h-[70vh] w-64 overflow-y-auto"
                    container={dialogContainer}
                  >
                    {/* "Try another version" — regenerate this asset from its
                        prompt in chat. Only for regenerable (AI-generated)
                        assets; uploaded images have no prompt to re-run. Lives
                        in the overflow menu, not a top-level pill (design review
                        2026-07-01 rejected a standalone button in the action row). */}
                    {props.onVariations && props.regenerable ? (
                      <DropdownMenuItem onClick={props.onVariations} disabled={editingDisabled}>
                        <ArrowsClockwiseIcon className="size-4" weight="regular" />
                        Try another version
                      </DropdownMenuItem>
                    ) : null}
                    {props.onUndo && props.canUndo ? (
                      <DropdownMenuItem onClick={props.onUndo} disabled={editingDisabled}>
                        <ArrowCounterClockwiseIcon className="size-4" weight="regular" />
                        Undo
                      </DropdownMenuItem>
                    ) : null}
                    {props.onRedo && props.canRedo ? (
                      <DropdownMenuItem onClick={props.onRedo} disabled={editingDisabled}>
                        <ArrowClockwiseIcon className="size-4" weight="regular" />
                        Redo
                      </DropdownMenuItem>
                    ) : null}
                    <DropdownMenuItem onClick={resolvedOnDownload} disabled={editingDisabled}>
                      <DownloadSimpleIcon className="size-4" weight="regular" />
                      Download
                    </DropdownMenuItem>
                    {props.artifactId ? (
                      (payload.kind === 'image' ? editingDisabled : isOperating) ? (
                        <DropdownMenuItem disabled>
                          <CardsThreeIcon className="size-4" weight="regular" />
                          Add to project
                        </DropdownMenuItem>
                      ) : (
                        <ProjectAssignmentSubmenu mediaIds={[props.artifactId]} />
                      )
                    ) : null}
                    <DropdownMenuSeparator />
                    <div className="space-y-3 px-2 py-1.5">{detailsBody}</div>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>
        ) : null}

        {/* ── Asset hero (stage) ──────────────────────────────────────
            Full-bleed, centered, object-contain (handled inside the stage).
            Crop / text-overlay modes still operate in-place. No floating
            chip over the asset — it duplicated the always-visible "Make
            video" pill in the action row below (caught in live review). */}
        <div className="relative flex min-h-0 flex-1 overflow-hidden">
          <MediaStudioAssetStage
            payload={payload}
            isOperating={isOperating}
            cropMode={cropMode}
            onCropCancel={() => setCropMode(false)}
            cropSaving={cropSaving}
            onCropSave={async (blob) => {
              setCropSaving(true);
              try {
                await props.onCropSave?.(blob);
                setCropMode(false);
              } finally {
                setCropSaving(false);
              }
            }}
            textOverlayMode={textOverlayMode}
            onTextOverlayCancel={() => setTextOverlayMode(false)}
            textOverlaySaving={textOverlaySaving}
            onTextOverlayLayersSave={handleLayersSave}
            overlayLayers={overlayLayers ?? undefined}
            overlayLayersAsTextElements={overlayLayersAsTextElements}
            onTextOverlaySave={async (blob) => {
              setTextOverlaySaving(true);
              try {
                if ((props as ImageDialogProps).onTextOverlaySave) {
                  await (props as ImageDialogProps).onTextOverlaySave?.(blob);
                } else if (props.onCropSave) {
                  await props.onCropSave(blob);
                }
                setTextOverlayMode(false);
              } finally {
                setTextOverlaySaving(false);
              }
            }}
          />
        </div>

        {/* ── Floating bottom bar ─────────────────────────────────────
            ONE scrolling action-pill row + composer, centered, capped at
            max-w-2xl. Hidden while a focused sub-editor (crop / text) owns
            the stage — those modes have their own Done/Cancel affordances. */}
        {!cropMode && !textOverlayMode ? (
          <div
            className="shrink-0 px-4 pb-3 pt-2"
            style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 0.75rem)' }}
          >
            <div className="mx-auto flex w-full max-w-2xl flex-col gap-2">
              <MediaStudioCustomPromptInput
                ref={composerRef}
                kind={payload.kind}
                isOperating={isOperating}
                value={composerDraft}
                onValueChange={setComposerDraft}
                onSubmit={async (prompt) => {
                  if (props.onCustomPrompt) await props.onCustomPrompt(prompt);
                }}
                quickActions={
                  // The fade is the ONLY affordance saying this row scrolls —
                  // `no-scrollbar` removes the other one. At 16px it was too
                  // narrow to read as a gradient, so the last chip appeared
                  // chopped mid-word ("Try lifesty|") and looked like a layout
                  // bug rather than more content. 32px is wide enough to read
                  // as a deliberate fade without swallowing a whole chip.
                  <div className="overflow-x-auto no-scrollbar [mask-image:linear-gradient(to_right,transparent,black_32px,black_calc(100%-32px),transparent)]">
                    <div className="flex w-max items-center gap-1.5 pr-4">
                      <span className="inline-flex h-8 shrink-0 items-center gap-1.5 px-1.5 text-caption font-medium text-skill/80">
                        <SparkleIcon className="size-3.5" weight="duotone" />
                        Try an edit
                      </span>
                      {payload.kind === 'image' ? (
                        suggestions
                          .filter((suggestion) => suggestion.id !== 'change-background')
                          .map((suggestion) => (
                            <button
                              key={suggestion.id}
                              type="button"
                              className="inline-flex h-8 shrink-0 items-center rounded-md px-1.5 text-caption text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                              onClick={() => handlePickSuggestion(suggestion)}
                              disabled={editingDisabled}
                            >
                              {suggestion.label}
                            </button>
                          ))
                      ) : (
                        <MediaStudioAISuggestionBlock
                          suggestions={suggestions}
                          onPickSuggestion={handlePickSuggestion}
                        />
                      )}
                      <SuggestionChipRow
                        kind={payload.kind}
                        composerMode
                        hideNamedActions={
                          payload.kind === 'video' && !(props as VideoDialogProps).onExtend
                            ? ['extend']
                            : undefined
                        }
                        onPrefill={handlePrefillChip}
                        onAction={(chip) => {
                          void props.onChipAction?.(chip);
                        }}
                        disabled={payload.kind === 'image' ? editingDisabled : isOperating}
                        activeId={activeOperationId ?? null}
                        colorLocked={colorLocked}
                      />
                    </div>
                  </div>
                }
                className="w-full"
              />
            </div>
          </div>
        ) : null}
      </div>
    </TooltipProvider>
  );
}

export function MediaStudioDialog(props: MediaStudioDialogProps): ReactNode {
  const { open, onOpenChange, inline } = props;

  if (inline) {
    // CRITICAL: when inline=true the parent (Inspector dialog) owns sizing
    // and the max-h cap. The previous `h-[100dvh] md:max-h-[80vh]` claimed
    // 100% of the device viewport on mobile, blowing past Inspector's
    // ~90vh bottom-sheet cap and clipping the bottom of the dialog —
    // which is where the toolbar lives. With the right rail's flex-col
    // sizing, that clipping made the toolbar disappear or float at
    // unpredictable positions ("can't see toolbar", "gap underneath",
    // "always at the top"). Defer all sizing to the parent: just be a
    // full-bleed transparent container.
    return (
      <div className="flex h-full w-full min-h-0 flex-col overflow-hidden">
        <DialogBody {...props} />
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* hideCloseButton: we render our own close button inline in the right-rail
          header so it sits flush against the collapse toggle. The radix-default
          floating X was in a different stacking layer + had a different icon size,
          which made the two buttons look mismatched even though both were size-8. */}
      <DialogContent
        size="full"
        className="h-[100dvh] sm:h-[80vh] sm:min-h-[600px] sm:max-h-[90vh] p-0 gap-0 overflow-hidden"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        hideCloseButton
      >
        <DialogTitle className="sr-only">Media Studio</DialogTitle>
        <DialogBody {...props} />
      </DialogContent>
    </Dialog>
  );
}
